import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { SelectionModel } from '@angular/cdk/collections';
import { CdkDrag, CdkDropList } from '@angular/cdk/drag-drop';
import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  ContentChild,
  ContentChildren,
  effect,
  EventEmitter,
  HostBinding,
  inject,
  input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  output,
  QueryList,
  signal,
  SimpleChanges,
  TemplateRef,
  TrackByFunction,
  ViewChild,
} from '@angular/core';
import { MatMenu } from '@angular/material/menu';
import { MatSort } from '@angular/material/sort';
import { MatTable } from '@angular/material/table';
import {
  ColumnConfigAdapter,
  ExtendDataTable,
  isExtendedDataTable,
  isGroupRowInDataTable,
  QtDataSource,
  valueAccessor,
} from '@qtek/libs/collections';
import { EmptyStateBase } from '@qtek/shared/directives';
import { Column, COLUMN_MIN_WIDTH } from '@qtek/shared/models';
import { indicatorRotate, isNonNullable } from '@qtek/shared/utils';
import { isObservable, Observable, Subject, Subscription } from 'rxjs';
import { take, tap } from 'rxjs/operators';
import {
  ColumnDefDirective,
  ColumnFormatDirective,
  HeaderDefDirective,
} from '../../directives';
import { DataTableConfig, defaultDataTableConfig } from '../../models';
import {
  getDuplicatedColumnFormatTemplateError,
  getDuplicatedColumnTemplateError,
} from '../../utils';

let nextUniqueId = 0;
@Component({
  selector: 'qt-data-table',
  templateUrl: './data-table.component.html',
  styleUrls: ['./data-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  animations: [indicatorRotate],
})
export class DataTableComponent<T = any>
  implements OnInit, AfterContentInit, OnDestroy, OnChanges
{
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly hostMatSort = inject(MatSort, { optional: true });

  /** Unique id, use it to distinguish tables between each other. */
  readonly uid = `qt-data-table-${nextUniqueId++}`;

  /** Data to display in table. */
  data = input<T[] | Observable<T[]> | QtDataSource<T>>();

  /** Whether data is loading or not. */
  loading = input(false);

  /** List of columns to display. */
  columns = input<Column[], Column[]>([], {
    transform: (value: Column[]) => this.normalizeColumns(value),
  });

  menuColumnDef = input(false, { transform: coerceBooleanProperty });

  /** Whether table should allow to select rows. */
  selectable = input(false);

  /** List of rows that should be selected. */
  selected = input<T[]>([]);

  isGroupingCollapsible = input(false);

  itemKey = input<keyof T>('_id' as keyof T);

  rowStylesCallback = input<(row: T) => { [key: string]: string }>(() => ({}));

  rowTrackByFn = input<TrackByFunction<T>>(
    (_index, row) => row?.[this.itemKey()]
  );

  warningRowIds = input<Set<string> | undefined>();

  tableConfig = input<Partial<DataTableConfig>, Partial<DataTableConfig>>(
    defaultDataTableConfig,
    {
      transform: (value: Partial<DataTableConfig>) => {
        return {
          ...defaultDataTableConfig,
          ...value,
        };
      },
    }
  );

  draggingPlaceholder = input<TemplateRef<any>>();

  /** Custom Angular template rendered as the CDK drag preview (ghost). */
  dragPreviewTemplate = input<TemplateRef<any>>();

  detailsTemplate = input<TemplateRef<any>>();

  isStandalonePopout = input(false);

  groupByExpandedRowColumns = input<Column[]>([]);

  sort = input<MatSort | null>(null);

  /**
   * Stream of row clicks. Emits clicked row data. If custom column definition
   * should handle click on its own, stop propagation of click events.
   * Otherwise this stream will emit new value.
   *
   * ```html
   * <qt-data-table [data]="data" [columns]="columns" (rowClick)="onRowClick($event)">
   *   <!-- calling `stopPropagation` will prevent `rowClick` from emitting -->
   *   <button *qtColumnDef="columnId" (click)="doSomething(); $event.stopPropagation()">Click me</button>
   * </qt-data-table>
   * ```
   */
  @Output() rowClick = new EventEmitter<T>();
  @Output() rowEventClick = new EventEmitter<{ data: T; event: MouseEvent }>();
  @Output() rowDoubleClick = new EventEmitter<T>();

  /** Emit when row is selected. */
  selectRow = output<T[]>();

  /** Emits drooped rows */
  droppedEvent = output<T[]>();

  /** Custom column definitions. */
  @ContentChildren(ColumnDefDirective)
  columnsDef: QueryList<ColumnDefDirective>;

  /** Custom column definitions by format */
  @ContentChildren(ColumnFormatDirective)
  formatsDef: QueryList<ColumnFormatDirective>;

  /** Custom header definitions. */
  @ContentChildren(HeaderDefDirective)
  headerDef: QueryList<HeaderDefDirective>;

  /**
   * Query for custom empty state. Used to determine whether
   * display default or custom one.
   */
  @ContentChild(EmptyStateBase)
  emptyState: EmptyStateBase;

  /** Menu to display in action column. */
  @ContentChild(MatMenu, { static: true })
  menu: MatMenu;

  @ViewChild(MatSort, { static: true })
  set matSort(sort: MatSort) {
    this._matSort = sort;
    this.updateDataSourceSort();
    this.setupSortSync();
  }
  private _matSort: MatSort | null = null;

  @ViewChild(MatTable) private _matTable: MatTable<T> | null = null;

  @HostBinding('class.sticky') get sticky() {
    return this.tableConfig().isHeaderSticky;
  }

  @HostBinding('class') hostClass = 'qt-data-table';

  // Computed signals for derived state
  columnsIds = computed(() => {
    const columns = this.columns();
    const sortedColumns = this.sortColumnsByHideOrder(columns);
    return this.getColumnsIds(sortedColumns);
  });

  /** Column widths when no adapter is present — cleared on component destroy. */
  private readonly _localWidths = signal<Record<string, number>>({});

  /** Column widths synced from the adapter state stream. */
  private readonly _adapterWidths = signal<Record<string, number>>({});

  /** Resolved ColumnConfigAdapter from data source registry (key 'columns'), if any. */
  private readonly _columnAdapter = signal<ColumnConfigAdapter | null>(null);

  // The ColumnConfigAdapter this component auto-registered (and its data
  // source), so the auto-register effect stays idempotent instead of churning.
  private _ownedColumnAdapter: ColumnConfigAdapter | null = null;
  private _ownedColumnAdapterDs: QtDataSource<T> | null = null;

  /** Effective width map: adapter-sourced when adapter present, else local. */
  private readonly _effectiveWidths = computed(() =>
    this._columnAdapter() ? this._adapterWidths() : this._localWidths()
  );

  /** Table data source. */
  set dataSource(value: QtDataSource<T>) {
    this.changedDataSubscription?.unsubscribe();

    this.changedDataSubscription = value
      .connect()
      .pipe(
        tap(() => {
          this.changeDetectorRef.markForCheck();
        })
      )
      .subscribe();

    this._dataSource.set(value);
    this.updateDataSourceSort();
    this.setupSortSync();
  }
  get dataSource(): QtDataSource<T> {
    return this._dataSource();
  }
  private readonly _dataSource = signal<QtDataSource<T>>(null);

  private changedDataSubscription: Subscription | null = null;
  private externalSortSub: Subscription | null = null;
  private internalSortSub: Subscription | null = null;
  private syncingFromExternal = false;
  private syncingFromInternal = false;
  private providedExternalSort: MatSort | null = null;

  /** Determines which row has detail row displayed. */
  details: SelectionModel<T>;

  /** Determines which rows are selected. */
  selection = computed(() => {
    return new SelectionModel(
      this.tableConfig().multiple,
      this.selected(),
      false,
      this.compareFn(this.itemKey())
    );
  });

  isGroupedBy = computed<string | undefined>(() => {
    const groupedById = this._dataSource()?.groupedById();
    if (!groupedById || typeof groupedById !== 'string') return undefined;
    return groupedById;
  });

  groupedByIdColumn = computed<Column | undefined>(() => {
    const groupedById: string | undefined = this.isGroupedBy();
    if (!groupedById) return undefined;
    return (
      this.columns().find(column => column.id === groupedById) ?? undefined
    );
  });

  /** Determines which grouping rows are expanded by value of groupBy. */
  expandedGroupRow: SelectionModel<string>;

  /**
   * Computed signal indicating if all groups are currently expanded.
   * Returns true when all groups are expanded, false otherwise.
   */
  readonly allGroupsExpanded = computed(() => {
    const allGroupsSize = this.allGroups().size;
    if (allGroupsSize === 0) return false;
    return allGroupsSize === this.expandedGroupsCount();
  });

  /**
   * Ids for expand, select and action columns. Should be exotic
   * enough so they won't collide with any id user may provide.
   */
  readonly expandId = '@@EXPAND';
  readonly selectId = '@@SELECT';
  readonly actionId = '@@ACTION';
  readonly groupId = '@@GROUPBY';
  readonly spacerId = '@@SPACER';

  /** Variable to keep state of dragging placeholder to hide "no data" message */
  isDragPlaceholder = false;

  /**
   * Contains custom templates that should be used for
   * columns defined by user.
   */
  readonly columnDefMap = new Map<string, TemplateRef<any>>();
  readonly formatDefMap = new Map<string, TemplateRef<any>>();
  readonly headerDefMap = new Map<string, TemplateRef<any>>();
  readonly compareFn = (itemKey: keyof T) => (a: T, b: T) => {
    if (a[itemKey] || b[itemKey]) {
      return a[itemKey] === b[itemKey];
    } else {
      return a === b;
    }
  };

  private readonly destroy$ = new Subject<void>();

  private dataSourceConnectionSubscription: Subscription;

  private clickTimeout: any;
  private readonly clickDelay = 200;

  selectedItems: T[];
  allGroups = signal<Set<string>>(new Set());
  private previousAllGroups = new Set<string>();
  private readonly expandedGroupsCount = signal<number>(0);

  canListBeDropped = (drag: CdkDrag, drop: CdkDropList) => {
    return this.tableConfig().droppable;
  };

  constructor() {
    effect(() => {
      this.allGroups.set(this.dataSource?.groups() ?? new Set());
    });

    this.initializeGroupExpansionEffect();

    effect(() => {
      const dataSource = this._dataSource();
      const columns = this.columns();
      if (dataSource) {
        dataSource.columns$.next(columns);
      }
    });

    effect(() => {
      this.providedExternalSort = this.sort();
      this.setupSortSync();
    });

    // Auto-register a ColumnConfigAdapter for resizable tables; MetaControls
    // picks it up via registry.changes$.
    //
    // MUST be idempotent: unregistering in the effect's onCleanup made every
    // re-run churn register/unregister, firing registry.changes$ mid-interaction
    // — which closed open popovers (SD-2374) and cancelled MetaControls'
    // debounced save, dropping unsaved control changes. So register once per
    // data source; tear down only when the data source changes or on destroy.
    effect(() => {
      const ds = this._dataSource();
      const want = !!ds && this.tableConfig().resizable;

      // Data source changed (or we no longer want an adapter) → release ours.
      if (
        this._ownedColumnAdapter &&
        (!want || this._ownedColumnAdapterDs !== ds)
      ) {
        if (
          this._ownedColumnAdapterDs?.registry.get('columns') ===
          this._ownedColumnAdapter
        ) {
          this._ownedColumnAdapterDs.registry.unregister('columns');
        }
        this._ownedColumnAdapter = null;
        this._ownedColumnAdapterDs = null;
      }

      // Want one, don't already own it, and none present → register once.
      if (
        want &&
        ds &&
        !this._ownedColumnAdapter &&
        !ds.registry.get('columns')
      ) {
        const adapter = new ColumnConfigAdapter();
        ds.registry.register(adapter);
        this._ownedColumnAdapter = adapter;
        this._ownedColumnAdapterDs = ds;
      }
    });

    // Track ColumnConfigAdapter from data source registry (key 'columns').
    // Re-resolves whenever the data source or its adapter set changes.
    effect(onCleanup => {
      const ds = this._dataSource();
      if (!ds) {
        this._columnAdapter.set(null);
        return;
      }
      this._columnAdapter.set(
        (ds.registry.get('columns') as ColumnConfigAdapter | undefined) ?? null
      );
      const sub = ds.registry.changes$.subscribe(() => {
        this._columnAdapter.set(
          (ds.registry.get('columns') as ColumnConfigAdapter | undefined) ??
            null
        );
      });
      onCleanup(() => sub.unsubscribe());
    });

    // Sync adapter state → _adapterWidths signal.
    effect(onCleanup => {
      const adapter = this._columnAdapter();
      if (!adapter) {
        this._adapterWidths.set({});
        return;
      }
      const sub = adapter.state$.subscribe(config => {
        this._adapterWidths.set(config.widths);
      });
      onCleanup(() => sub.unsubscribe());
    });
  }

  /** Returns the effective display width for a column (user-set or column.minWidth). */
  getColumnWidth(column: Column): number {
    const widths = this._effectiveWidths();
    return widths[column.id] ?? column.minWidth;
  }

  /**
   * Returns the effective max-width for a column.
   * After resize: locked to the user-set width.
   * Before resize: uses column.maxWidth (or null = unconstrained).
   */
  getColumnMaxWidth(column: Column): number | null {
    const widths = this._effectiveWidths();
    const resized = widths[column.id];
    if (resized != null) return resized;
    return column.maxWidth ?? null;
  }

  /** Called by the resize directive on mouseup with the final column width. */
  onColumnResize(colId: string, width: number): void {
    const adapter = this._columnAdapter();
    if (adapter) {
      adapter.setWidth(colId, width);
    } else {
      this._localWidths.update(w => ({ ...w, [colId]: width }));
    }
    // DOM widths are already correct (directive applied them during drag).
    // Re-run sticky-styler so left/right offsets reflect the new column sizes.
    this._matTable?.updateStickyColumnStyles();
  }

  ngOnInit() {
    this.details = new SelectionModel<T>(
      true,
      [],
      null,
      this.compareFn(this.itemKey())
    );
    this.expandedGroupRow = new SelectionModel<string>(true, []);
  }

  ngAfterContentInit() {
    this.setColumnTemplateRefs();
    this.setFormatTemplateRefs();
    this.setHeaderTemplateRefs();
  }

  ngOnChanges(changes: SimpleChanges) {
    const { data } = changes;

    /** When data update then rerender table. */
    if (data) {
      const { currentValue, firstChange } = data;
      this.dataSourceConnectionSubscription?.unsubscribe();

      if (currentValue instanceof QtDataSource) {
        this.dataSource = currentValue;
      } else {
        /** Check if data is of correct type. */
        const isValid =
          isObservable(currentValue) || Array.isArray(currentValue);

        if (firstChange) {
          /** Init data source with empty array if value is invalid. */
          this.dataSource = new QtDataSource(
            isValid ? (currentValue as T[]) : []
          );
        } else if (isValid) {
          /** Only update data when it's valid. */
          this.dataSource.data = currentValue as T[];
        }
      }

      this.dataSourceConnectionSubscription = this.dataSource
        .connect()
        .pipe(
          tap(() => {
            this.refresh();
          })
        )
        .subscribe();
    }
  }

  onRowClick(row: any, event: MouseEvent): void {
    if (this.clickTimeout) {
      clearTimeout(this.clickTimeout);
      this.clickTimeout = null;
      return;
    }

    this.clickTimeout = setTimeout(() => {
      this.rowClick.emit(row);
      this.clickTimeout = null;
    }, this.clickDelay);
  }

  onRowDoubleClick(row: any, event: MouseEvent): void {
    if (this.clickTimeout) {
      clearTimeout(this.clickTimeout);
      this.clickTimeout = null;
    }

    event.preventDefault();
    event.stopPropagation();

    this.rowDoubleClick.emit(row);
  }

  ngOnDestroy() {
    this.changedDataSubscription?.unsubscribe();
    this.dataSourceConnectionSubscription?.unsubscribe();
    this.externalSortSub?.unsubscribe();
    this.internalSortSub?.unsubscribe();
    // Release the adapter we auto-registered (no longer done in the effect).
    if (
      this._ownedColumnAdapter &&
      this._ownedColumnAdapterDs?.registry.get('columns') ===
        this._ownedColumnAdapter
    ) {
      this._ownedColumnAdapterDs.registry.unregister('columns');
    }
    this._ownedColumnAdapter = null;
    this._ownedColumnAdapterDs = null;
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Method that is fired on dropping item at table  */
  dropped(event: any) {
    if (!this.tableConfig().droppable) return;
    this.isDragPlaceholder = false;
    this.droppedEvent.emit(event);
  }

  /** Function to change dragging placeholder state, to hide "no data" message */
  dragEntered() {
    if (!this.tableConfig().droppable) return;
    this.isDragPlaceholder = true;
  }

  /** Function to change dragging placeholder state, to show "no data" message */
  dragExited() {
    if (!this.tableConfig().droppable) return;
    this.isDragPlaceholder = false;
  }

  /**
   * trackBy function for columns.
   */
  trackByColumns(index: number, item: Column) {
    return item.id;
  }

  /**
   * Trigger change detection.
   */
  refresh() {
    this.changeDetectorRef.markForCheck();
  }

  /**
   * Whether the number of selected elements matches the total number of rows.
   *
   * @param selectionModel Model to check against the data.
   */
  isAllSelected(selectionModel: SelectionModel<T>) {
    const selectableCount = this.dataSource.renderData.value.filter(row =>
      this.isRowSelectable(row)
    ).length;
    return selectionModel.selected.length === selectableCount;
  }

  /**
   * Whether the given row may be selected, per the optional
   * {@link DataTableConfig.isRowSelectable} predicate. Defaults to true so
   * tables that don't configure it behave exactly as before.
   */
  isRowSelectable(row: T): boolean {
    const predicate = this.tableConfig().isRowSelectable;
    return predicate ? predicate(row) : true;
  }

  /**
   * Toggle singe row for given model.
   *
   * @param selectionModel Instance of selection model to use.
   * @param row Reference to the row which should be toggled.
   */
  toggle(selectionModel: SelectionModel<T>, row: T) {
    if (!this.isRowSelectable(row)) {
      return;
    }
    if (selectionModel.isSelected(row)) {
      selectionModel.deselect(
        selectionModel.selected.find((selectedItem: T) => {
          return this.compareFn(this.itemKey())(selectedItem, row);
        })
      );
    } else {
      selectionModel.select(row);
    }
    this.selectRow.emit(selectionModel.selected);
  }

  /**
   * Toggle every row for given model.
   *
   * @param selectionModel Instance of selection model to use.
   */
  toggleAll(selectionModel: SelectionModel<T>) {
    if (this.isAllSelected(selectionModel)) {
      selectionModel.clear();
    } else {
      this.dataSource.renderData.value.forEach(row => {
        if (this.isRowSelectable(row)) {
          selectionModel.select(row);
        }
      });
    }

    this.selectRow.emit(selectionModel.selected);
  }

  toggleGroupByRow(row: ExtendDataTable): void {
    if (
      !this.groupByExpandedRowColumns?.length &&
      !this.isGroupingCollapsible()
    ) {
      return;
    }

    if (this.expandedGroupRow.isSelected(row.__groupBy)) {
      this.expandedGroupRow.deselect(row.__groupBy);
    } else {
      this.expandedGroupRow.select(row.__groupBy);
      this.details.clear();
    }
    this.expandedGroupsCount.set(this.expandedGroupRow.selected.length);
  }

  /**
   * Toggle all group rows. If all groups are expanded, collapses them.
   * If not all groups are expanded, expands all groups.
   */
  toggleAllGroupBy(): void {
    this.dataSource
      ?.connect()
      .pipe(take(1))
      .subscribe(data => {
        const allGroups = this.allGroups();
        const isAllSelected =
          allGroups.size === this.expandedGroupRow.selected.length;
        if (isAllSelected) {
          this.expandedGroupRow.clear();
        } else {
          this.details.clear();
          data.forEach(row => {
            if (isExtendedDataTable(row))
              this.expandedGroupRow.select(row.__groupBy);
          });
        }
        this.expandedGroupsCount.set(this.expandedGroupRow.selected.length);
        this.changeDetectorRef.markForCheck();
      });
  }

  isGroupRowExpanded(
    selectionModel: SelectionModel<string>,
    row: any
  ): boolean {
    return isExtendedDataTable(row) && selectionModel.isSelected(row.__groupBy);
  }

  public get valueAccessorFn() {
    return this._dataSource()?.valueAccessorFn ?? (() => null);
  }

  valueAccessorForExpandedGroupBy(row: T, path: string): any {
    return valueAccessor(row, path.replace('__groupBy.', ''));
  }

  isGroupRow(index: number, rowData: T) {
    return isGroupRowInDataTable(rowData);
  }

  isNotGroupRow(index: number, rowData: T) {
    return !isGroupRowInDataTable(rowData);
  }

  private getColumnsIds(sortedColumns: Column[]) {
    const ids = sortedColumns
      .filter(column => !column.hidden)
      .map(column => column.id);

    ids.push(this.spacerId);

    if (this.menu || this.menuColumnDef()) {
      ids.push(this.actionId);
    }

    const hasGroupBy = isNonNullable(this._dataSource()?.groupedById());

    if (hasGroupBy) {
      ids.unshift(this.groupId);
    }

    if (this.detailsTemplate()) {
      if (hasGroupBy) {
        ids.splice(1, 0, this.expandId);
      } else {
        ids.unshift(this.expandId);
      }
    }

    if (this.tableConfig().selectable && !this.tableConfig().hideSelect) {
      ids.unshift(this.selectId);
    }

    return ids;
  }

  private updateDataSourceSort() {
    const dataSource = this._dataSource();
    if (dataSource && this._matSort) {
      dataSource.setControls({ sort: this._matSort });
    }
  }

  private setupSortSync() {
    const externalSort = this.providedExternalSort || this.hostMatSort;
    if (!externalSort || !this._matSort) {
      this.externalSortSub?.unsubscribe();
      this.internalSortSub?.unsubscribe();
      return;
    }

    this.externalSortSub?.unsubscribe();
    this.internalSortSub?.unsubscribe();

    const external = externalSort;
    const internal = this._matSort;

    this.externalSortSub = external.sortChange.subscribe(sort => {
      if (this.syncingFromInternal) {
        return;
      }

      this.syncingFromExternal = true;
      internal.active = sort.active;
      internal.direction = sort.direction;
      internal.sortChange.emit(sort);
      this.syncingFromExternal = false;
    });

    this.internalSortSub = internal.sortChange.subscribe(sort => {
      if (this.syncingFromExternal) {
        return;
      }

      this.syncingFromInternal = true;
      external.active = sort.active;
      external.direction = sort.direction;
      external.sortChange.emit(sort);
      this.syncingFromInternal = false;
    });

    if (external.active && external.direction) {
      this.syncingFromExternal = true;
      internal.active = external.active;
      internal.direction = external.direction;
      internal.sortChange.emit({
        active: external.active,
        direction: external.direction,
      });
      this.syncingFromExternal = false;
    }
  }

  /**
   * Returns normalized columns, so they are safe to use.
   *
   * @param columns List of columns to process.
   */
  private normalizeColumns(columns: Column[]): Column[] {
    return columns.map(column => {
      /** Fallback to default minimum width and hide order, if not specified. */
      const normalizedColumn = {
        ...column,
        minWidth: isNonNullable(column.minWidth)
          ? column.minWidth
          : COLUMN_MIN_WIDTH,
        hideOrder: isNonNullable(column.hideOrder) ? column.hideOrder : 0,
      } as Column;

      return normalizedColumn;
    });
  }

  /**
   * Sort columns by `hideOrder`, ascending.
   */
  private sortColumnsByHideOrder(columns: Column[]) {
    return columns.slice().sort((a, b) => a.hideOrder - b.hideOrder);
  }

  /**
   * Set custom column definitions to corresponding maps.
   */
  private setColumnTemplateRefs() {
    for (const { id, template } of this.columnsDef.toArray()) {
      if (this.columnDefMap.has(id)) {
        throw getDuplicatedColumnTemplateError(id);
      }

      this.columnDefMap.set(id, template);
    }
  }

  /**
   * Set custom column format definitions to corresponding maps.
   */
  private setFormatTemplateRefs() {
    for (const { format, templateRef } of this.formatsDef.toArray()) {
      if (this.formatDefMap.has(format)) {
        throw getDuplicatedColumnFormatTemplateError(format);
      }

      this.formatDefMap.set(format, templateRef);
    }
  }

  private setHeaderTemplateRefs() {
    for (const { id, template } of this.headerDef.toArray()) {
      this.headerDefMap.set(id, template);
    }
  }

  protected getClickSelected(selection: any, row: any) {
    return () => {
      this.toggle(selection, row);
      this.selectRow.emit(selection.selected);
    };
  }

  private initializeGroupExpansionEffect() {
    effect(() => {
      const currentAllGroups = this.allGroups();

      // Skip first run when expandedGroupRow is not yet initialized
      if (!this.expandedGroupRow) return;

      const previousAllGroups = this.previousAllGroups;
      const intersection = new Set(
        [...previousAllGroups].filter(value => currentAllGroups.has(value))
      );
      const previousSelected = new Set(
        [...this.expandedGroupRow.selected].filter(value =>
          previousAllGroups.has(value)
        )
      );

      const isNewGroupSelected =
        previousAllGroups.size &&
        !intersection.size &&
        previousSelected.size === previousAllGroups.size;

      if (isNewGroupSelected) {
        currentAllGroups.forEach(value => {
          this.expandedGroupRow.select(value);
        });
      }

      this.expandedGroupRow.selected.forEach((selected: string) => {
        if (!currentAllGroups.has(selected))
          this.expandedGroupRow.deselect(selected);
      });

      this.expandedGroupsCount.set(this.expandedGroupRow.selected.length);
      this.previousAllGroups = new Set(currentAllGroups);
    });
  }
}
